import React from 'react';

import {
  getManagedCdqLead,
  ManagedCdqLeadDetail,
  ManagedLeadField,
} from '../../services/api/managed-leads/managedLeads';
import {
  ManagedLeadDetailDatum,
  ManagedLeadDetailSection,
} from './components/ManagedLeadDetailSection';
import ManagedProductLeadDetailPage from './components/ManagedProductLeadDetailPage';
import { formatAdvancementDatum } from './managedLeadAdvancementData';
import {
  formatManagedLeadCurrency,
  formatManagedLeadField,
  getManagedLeadFieldValue,
} from './managedLeadFormatting';

const CDQ_OCCUPATION_LABELS: Record<string, string> = {
  dipendente_pubblico: 'Dipendente pubblico',
  dipendente_statale: 'Dipendente statale',
  dipendente_privato: 'Dipendente privato',
  dipendente_parapubblico: 'Dipendente parapubblico',
  pensionato: 'Pensionato',
};

/**
 * Only a value the backend recognised as a known occupation gets a label: an
 * unknown enum keeps its raw value instead of being guessed or hidden.
 */
const formatOccupation = (field: ManagedLeadField<string>) =>
  field.state === 'value'
    ? CDQ_OCCUPATION_LABELS[field.value] ?? field.value
    : formatManagedLeadField(field);

const formatMonthlyIncomeFrequency = (field: ManagedLeadField<string>) =>
  field.state === 'value'
    ? `${field.value} mensilità`
    : formatManagedLeadField(field);

const ManagedCdqDetailPage = () => (
  <ManagedProductLeadDetailPage<ManagedCdqLeadDetail>
    product="cessioni-del-quinto"
    productName="Cessioni del quinto"
    loadingMessage="Caricamento dettaglio cessione del quinto…"
    getLead={getManagedCdqLead}
    renderProductSections={(detail, requestValuesAction) => (
      <>
        <ManagedLeadDetailSection title="Informazioni personali">
          <ManagedLeadDetailDatum
            label="Occupazione"
            value={formatOccupation(detail.fields.occupation)}
          />
          <ManagedLeadDetailDatum
            label="Età"
            value={formatManagedLeadField(detail.fields.age)}
          />
          <ManagedLeadDetailDatum
            label="SPID"
            value={formatManagedLeadField(detail.fields.spid)}
          />
          <ManagedLeadDetailDatum
            label="Cittadinanza italiana"
            value={formatManagedLeadField(detail.fields.citizenship)}
          />
          {detail.fields.employeesNumber.state !== 'not_applicable' && (
            <ManagedLeadDetailDatum
              label="Numero dipendenti azienda"
              value={formatManagedLeadField(detail.fields.employeesNumber)}
            />
          )}
          {detail.fields.companySeniority.state !== 'not_applicable' && (
            <ManagedLeadDetailDatum
              label="Anzianità nell'azienda attuale"
              value={formatManagedLeadField(detail.fields.companySeniority)}
            />
          )}
          <ManagedLeadDetailDatum
            label="Impegni attuali"
            value={formatManagedLeadField(detail.fields.existingCommitments)}
          />
          <ManagedLeadDetailDatum
            label="Segnalazioni CRIF"
            value={formatManagedLeadField(detail.fields.latePayments)}
          />
        </ManagedLeadDetailSection>

        <ManagedLeadDetailSection
          title="Valori cessione del quinto"
          action={requestValuesAction}
        >
          <ManagedLeadDetailDatum
            label="Importo richiesto"
            value={formatManagedLeadCurrency(
              getManagedLeadFieldValue(detail.fields.amount)
            )}
          />
          <ManagedLeadDetailDatum
            label="Reddito mensile"
            value={formatManagedLeadCurrency(
              getManagedLeadFieldValue(detail.fields.income)
            )}
          />
          <ManagedLeadDetailDatum
            label="Mensilità reddito"
            value={formatMonthlyIncomeFrequency(
              detail.fields.monthlyIncomeFrequency
            )}
          />
          <ManagedLeadDetailDatum
            label="Data liquidazione"
            value={formatAdvancementDatum(
              detail.advancementData,
              'liquidationDate',
              'date'
            )}
          />
        </ManagedLeadDetailSection>
      </>
    )}
  />
);

export default ManagedCdqDetailPage;
